// frontend/src/components/Common/TenantHelmet.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTenant } from "../../context/TenantContext";

const TenantHelmet = () => {
  const { tenant, loading } = useTenant();

  // Update favicon and meta tags directly as a fallback
  useEffect(() => {
    if (!loading && tenant) {
      // Determine favicon URL
      let faviconUrl = tenant.Favicon || "/icon-shoping.ico";
      
      if (tenant.Logo) {
        faviconUrl = tenant.Logo;
      } else if (tenant.TenantCode) {
        faviconUrl = `/api/tenant-logo/${tenant.TenantCode}`;
      }

      // Update favicon
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = faviconUrl;

      // Update apple-touch-icon
      let appleLink = document.querySelector("link[rel~='apple-touch-icon']");
      if (!appleLink) {
        appleLink = document.createElement("link");
        appleLink.rel = "apple-touch-icon";
        document.head.appendChild(appleLink);
      }
      appleLink.href = faviconUrl;

      // Update shortcut icon
      let shortcutLink = document.querySelector("link[rel~='shortcut icon']");
      if (!shortcutLink) {
        shortcutLink = document.createElement("link");
        shortcutLink.rel = "shortcut icon";
        document.head.appendChild(shortcutLink);
      }
      shortcutLink.href = faviconUrl;

      // Update theme color
      if (tenant.PrimaryColor) {
        let metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (!metaThemeColor) {
          metaThemeColor = document.createElement("meta");
          metaThemeColor.name = "theme-color";
          document.head.appendChild(metaThemeColor);
        }
        metaThemeColor.content = tenant.PrimaryColor;
      }

      // Update document title
      document.title = tenant.BusinessName 
        ? `${tenant.BusinessName} - Shop Online` 
        : "TF Online";
    }
  }, [tenant, loading]);

  if (loading || !tenant) {
    return (
      <Helmet>
        <title>Loading...</title>
      </Helmet>
    );
  }

  const businessName = tenant.BusinessName || "Online Store";
  const metaTitle = tenant.MetaTitle || `${businessName} - Shop Online`;
  const metaDescription =
    tenant.MetaDescription ||
    `Shop at ${businessName} for quality products and services.`;
  
  let faviconUrl = tenant.Favicon || "/icon-shoping.ico";
  if (tenant.Logo) {
    faviconUrl = tenant.Logo;
  } else if (tenant.TenantCode) {
    faviconUrl = `/api/tenant-logo/${tenant.TenantCode}`;
  }

  const primaryColor = tenant.PrimaryColor || "#2c7da0";
  const logo = tenant.Logo || null;
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const canonicalUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";
  const googleVerification = tenant.settings?.GoogleSiteVerification;
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "OnlineStore",
    name: businessName,
    url: typeof window !== "undefined" ? window.location.origin : "",
    image: logo || undefined,
    telephone: tenant.BusinessPhone || undefined,
    email: tenant.BusinessEmail || undefined,
    address: tenant.FullAddress
      ? {
          "@type": "PostalAddress",
          streetAddress: [tenant.AddressLine1, tenant.AddressLine2]
            .filter(Boolean)
            .join(", "),
          addressLocality: tenant.City || undefined,
          addressRegion: tenant.State || undefined,
          postalCode: tenant.PostalCode || undefined,
          addressCountry: tenant.Country || undefined,
        }
      : undefined,
    sameAs: [
      tenant.SocialFacebook,
      tenant.SocialInstagram,
      tenant.SocialTwitter,
      tenant.SocialLinkedIn,
    ].filter(Boolean),
  };

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="theme-color" content={primaryColor} />
      {googleVerification && (
        <meta
          name="google-site-verification"
          content={googleVerification}
        />
      )}

      {/* Favicon - Use Tenant Logo */}
      <link rel="icon" href={faviconUrl} />
      <link rel="shortcut icon" href={faviconUrl} />
      <link rel="apple-touch-icon" href={faviconUrl} />

      {/* Open Graph / Social Media */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      {logo && <meta property="og:image" content={logo} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {logo && <meta name="twitter:image" content={logo} />}

      {/* Additional Meta */}
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonicalUrl} />
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
};

export default TenantHelmet;